// What the scoresheet panel shows for a review state, without React: the
// game's moves (from `state.game`, parsed once by the review), their grades
// from the review's scan, the move on the board, the move a line branches
// after, and what the board's constraints conceal or switch off. The panel
// in `index.tsx` reads the review and the constraints and draws this.
import type { San } from '@chess/api-model';
import type { GameScan, ReviewGame, ReviewState, ReviewStatus } from '@chess/api-review';
import { judgementsOf, type Judgements } from './judgements.ts';

/** The constraints the sheet honours, as the registry combines them. */
export interface SheetConstraints {
  /** Scoresheet visibility: moves from this index on are a puzzle's answer. */
  hideFrom?: number;
  /** Controls: whether choosing a move is allowed. */
  scoresheetClicks: boolean;
}

export interface SheetView {
  status: ReviewStatus;
  /** What the panel says instead of the sheet; absent when the game is ready. */
  message?: string;
  moves: readonly San[];
  judgements: Judgements;
  /** The game ply the reader's own line starts from. */
  branchPly?: number;
  /** The ply whose move is highlighted; absent while the board is off the game. */
  currentPly?: number;
  /** How many moves the reader's line has; the scroll follows it. */
  lineLength: number;
  concealFrom?: number;
  /** Whether a move can be chosen. */
  clickable: boolean;
}

const MESSAGES: Record<Exclude<ReviewStatus, 'ready'>, string> = {
  idle: 'No game open.',
  loading: 'Loading…',
  missing: 'This game is not in the library.',
  failed: 'The game could not be read.',
};

const NO_MOVES: readonly San[] = [];
const NO_JUDGEMENTS: Judgements = new Map();

/**
 * The game ply whose move is highlighted: the ply on the board, or, while
 * the reader is out on a line of their own, none (the line is not on the
 * sheet; its branch is marked instead). While the reader looks back along
 * the path (`state.browse`, as in a puzzle), the ply looked at, when it is
 * still in the game rather than in the line.
 */
export function currentPly(state: ReviewState): number | undefined {
  if (state.browse !== undefined) return state.exploring && state.browse > state.exploring.fromPly ? undefined : state.browse;
  return state.exploring ? undefined : state.ply;
}

// The moves and the grades are worked out once per game and per scan, not on
// every state the review sends.
const sansCache = new WeakMap<ReviewGame, readonly San[]>();
const gradesCache = new WeakMap<GameScan, { game: ReviewGame; judgements: Judgements }>();

function sansOf(game: ReviewGame): readonly San[] {
  let sans = sansCache.get(game);
  if (!sans) sansCache.set(game, (sans = game.moves.map((m) => m.san)));
  return sans;
}

/** The grades of the game's moves from the review's scan, when the scan is this game's. */
export function gradesOf(scan: GameScan | undefined, game: ReviewGame): Judgements {
  if (!scan || scan.gameId !== game.id) return NO_JUDGEMENTS;
  const cached = gradesCache.get(scan);
  if (cached && cached.game === game) return cached.judgements;
  const judgements = judgementsOf(scan, game.moves);
  gradesCache.set(scan, { game, judgements });
  return judgements;
}

/** The sheet for a review state under the board's constraints. */
export function sheetView(state: ReviewState, constraints: SheetConstraints): SheetView {
  const game = state.status === 'ready' ? state.game : undefined;
  const common = { status: state.status, concealFrom: constraints.hideFrom, clickable: constraints.scoresheetClicks };
  if (!game) {
    // A ready state without its game is not one the review sends; read it as failed.
    const status = state.status === 'ready' ? 'failed' : state.status;
    return { ...common, status, message: MESSAGES[status], moves: NO_MOVES, judgements: NO_JUDGEMENTS, lineLength: 0 };
  }
  return {
    ...common,
    moves: sansOf(game),
    judgements: gradesOf(state.scan, game),
    branchPly: state.exploring?.fromPly,
    currentPly: currentPly(state),
    lineLength: state.exploring?.moves.length ?? 0,
  };
}
